import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Alert,
  AnchorButton,
  Box,
  CheckboxStateless,
  Flex,
  H2,
  I18NRemove,
  LoadableButton,
  Text
} from '../../../../components';
import {
  CurrentUser,
  CurrentUser_paymentMethod,
  PartnerId,
  PaymentDocumentType,
  PaymentMethodType,
  payOrderChargeMutation,
  payOrderChargeMutation_payOrderCharge_ConfirmationSessionStripe,
  payOrderChargeMutationVariables,
  payOrderCheckoutMutation,
  payOrderCheckoutMutation_payOrderCheckout_CheckoutSessionStripe,
  payOrderCheckoutMutationVariables,
  PlaceSubscriptionOrder,
  PlaceSubscriptionOrder_discount_partner,
  placeSubscriptionOrderMutation,
  placeSubscriptionOrderMutationVariables,
  productBySlugQuery_productBySlug,
  ProductOptMethod,
  revokeDiscountCodeMutation,
  revokeDiscountCodeMutationVariables,
  settings_system_paymentProvider
} from '../../../../core-types';
import {
  PAY_ORDER_CHARGE_MUTATION,
  PAY_ORDER_CHECKOUT_MUTATION,
  PLACE_SUBSCRIPTION_ORDER_FRAGMENT,
  PLACE_SUBSCRIPTION_ORDER_MUTATION,
  REVOKE_DISCOUNT_CODE_MUTATION
} from '../graphql';
import {
  ApolloError,
  defaultDataIdFromObject,
  MutationResult,
  useMutation
} from '@apollo/client';
import { messages } from '../messages';
import { FormattedCurrency } from '../../../../components/FormattedCurrency';
import { pushRoute } from '../../../../components/Link';
import { StripeCheckoutRedirect } from '../../../StripeCheckoutRedirect';
import { GraphQLErrors } from '../../../../components/GraphQLErrors';
import { StripeConfirmCardPayment } from '../../../StripeConfirmCardPayment';
import { useSettingsQuery } from '../../../../lib/core/SettingsQuery';
import { CURRENT_USER_FRAGMENT } from '../../../../lib/core/fragments/currentUser';
import { ChargePaymentCardMessage } from '../../../../components/ChargePaymentCardMessage';
import { Discount } from '../Discount';
import {
  ProductPrice,
  RegistrationFee
} from '../../../../components/ProductPlanCard';
import { AnimatePresence, motion } from 'framer-motion';
import { DiscountAmount } from '../../../../components/DiscountAmount';
import { Form, Formik, useField, useFormikContext } from 'formik';
import { PaymentProviderInfo } from '../../../../components/PaymentProviderInfo';
import { useRouter } from 'next/router';
import { PartnersForm } from '../../../Partners/PartnersForm';
import { LOADABLE_BUTTON_DEFAULT_SUCCESS_TIMEOUT_MS } from '@urbaninfrastructure/react-ui-kit';
import { AtmCardPreview } from '../../../Partners/AtmCardPreview';
import { PaymentMethodTypeList } from '../../../../components/PaymentMethodTypeList';

interface Values {
  autoRenewProduct: boolean;
  paymentMethodType: PaymentMethodType | null;
  changePaymentMethod: boolean;
}

const RenewOptMethod = ({
  product
}: {
  product: productBySlugQuery_productBySlug;
}) => {
  const [field] = useField<Values['autoRenewProduct']>({
    name: 'autoRenewProduct',
    type: 'checkbox'
  });

  if (!product.price) {
    return null;
  }

  return (
    <Box mb="xs" maxWidth={400} textAlign="left" data-testid="RenewOptMethod">
      {(product.renewOptMethod === ProductOptMethod.in ||
        product.renewOptMethod === ProductOptMethod.out) && (
        <CheckboxStateless
          checked={field.checked}
          onChange={field.onChange}
          onBlur={field.onBlur}
          name={field.name}
          label={
            <FormattedMessage
              id="renew_automatically"
              defaultMessage="Renew automatically"
            />
          }
        />
      )}
      <AnimatePresence initial={false}>
        {field.checked && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
          >
            <Text data-testid="RenewOptMethod.description" small mt={2}>
              <FormattedMessage
                id="subscription_renewal_scheduled_after_activation"
                defaultMessage="Renewal will be scheduled after subscription activation."
              />{' '}
              <FormattedMessage
                id="purchase_edit_anytime"
                defaultMessage="You can edit this any time after the purchase."
              />
            </Text>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

function DiscountPartnerIcon({
  partner
}: {
  partner: PlaceSubscriptionOrder_discount_partner;
}) {
  switch (partner.id) {
    case PartnerId.milan_atm: {
      return <AtmCardPreview width={30} />;
    }
    default: {
      return null;
    }
  }
}

function getPaths({
  asPath,
  token,
  type
}: {
  asPath: string;
  token: string;
  type: PaymentDocumentType;
}) {
  return {
    successPath: `${asPath}&state=success&token=${token}&type=${type}`,
    cancelPath: asPath
  };
}

export const CheckoutStep = ({
  paymentProvider,
  currentUser,
  product,
  setRequestError,
  setPaymentError,
  disclaimer,
  haveImage,
  availablePaymentMethods
}: {
  paymentProvider: settings_system_paymentProvider;
  currentUser: CurrentUser;
  setRequestError: (error: ApolloError) => void;
  setPaymentError: (message: string) => void;
  disclaimer: React.ReactNode;
  product: productBySlugQuery_productBySlug;
  haveImage: boolean;
  availablePaymentMethods: PaymentMethodType[];
}) => {
  const router = useRouter();
  const intl = useIntl();
  const [checkoutSessionStripe, setCheckoutSessionStripe] = useState<
    payOrderCheckoutMutation_payOrderCheckout_CheckoutSessionStripe | undefined
  >();
  const [
    confirmationSessionStripeData,
    setConfirmationSessionStripeData
  ] = useState<
    payOrderChargeMutation_payOrderCharge_ConfirmationSessionStripe | undefined
  >();
  const [
    confirmationSessionStripeError,
    setConfirmationSessionStripeError
  ] = useState<boolean>(false);
  const [order, setOrder] = useState<PlaceSubscriptionOrder | undefined>();
  const { refetch, client } = useSettingsQuery();

  const { paymentMethod } = currentUser;

  const [payOrderCheckout, payOrderCheckoutResult] = useMutation<
    payOrderCheckoutMutation,
    payOrderCheckoutMutationVariables
  >(PAY_ORDER_CHECKOUT_MUTATION, {
    onCompleted: data => {
      switch (data.payOrderCheckout.__typename) {
        case 'CheckoutSessionStripe': {
          setCheckoutSessionStripe(data.payOrderCheckout);
          break;
        }
        case 'CheckoutSessionPayex': {
          window.location.replace(data.payOrderCheckout.redirectUrl);
          break;
        }
      }
    },
    onError: error => {
      setRequestError(error);
    }
  });

  const [payOrderCharge, payOrderChargeResult] = useMutation<
    payOrderChargeMutation,
    payOrderChargeMutationVariables
  >(PAY_ORDER_CHARGE_MUTATION, {
    onCompleted: async data => {
      switch (data.payOrderCharge.__typename) {
        case 'PaymentIntentError': {
          setPaymentError(data.payOrderCharge.message);
          break;
        }
        case 'ChargeCompletedSubscription': {
          const fragmentId =
            defaultDataIdFromObject({
              id: currentUser.id,
              __typename: currentUser.__typename
            }) || `${currentUser.__typename}:${currentUser.id}`;
          client.writeFragment<CurrentUser>({
            id: fragmentId,
            fragmentName: 'CurrentUser',
            fragment: CURRENT_USER_FRAGMENT,
            data: {
              ...currentUser,
              currentSubscription: data.payOrderCharge.subscription
            }
          });
          window.location.replace(
            `/post-checkout?locale=${intl.locale}${
              order
                ? `&token=${order.token}&type=${PaymentDocumentType.order}`
                : ''
            }`
          );
          break;
        }
        case 'ChargeCompleted': {
          pushRoute({
            intl,
            route: 'how-it-works'
          });
          break;
        }
        case 'ConfirmationSessionStripe': {
          setConfirmationSessionStripeData(data.payOrderCharge);
          break;
        }
        case 'RequirePaymentMethodStripe': {
          //TODO: only used for native apps
          break;
        }
      }
    }
  });

  const [placeSubscriptionOrder, placeSubscriptionOrderResult] = useMutation<
    placeSubscriptionOrderMutation,
    placeSubscriptionOrderMutationVariables
  >(PLACE_SUBSCRIPTION_ORDER_MUTATION, {
    onCompleted: ({ placeSubscriptionOrder }) => {
      if (placeSubscriptionOrder.__typename === 'SubscriptionOrderError') {
        setPaymentError(placeSubscriptionOrder.message);
      } else if (placeSubscriptionOrder.__typename === 'Order') {
        setOrder(placeSubscriptionOrder);
      }
    },
    onError: error => {
      setRequestError(error);
    }
  });

  const [revokeDiscountCode, revokeDiscountCodeResult] = useMutation<
    revokeDiscountCodeMutation,
    revokeDiscountCodeMutationVariables
  >(REVOKE_DISCOUNT_CODE_MUTATION, {
    onCompleted: ({ revokeDiscountCode }) => {
      setTimeout(() => {
        setOrder(revokeDiscountCode);
      }, LOADABLE_BUTTON_DEFAULT_SUCCESS_TIMEOUT_MS);
    },
    onError: error => {
      setRequestError(error);
    }
  });

  useEffect(() => {
    placeSubscriptionOrder({ variables: { productId: product.id } });
  }, []);

  let price = 0;
  if (order) {
    if (order.discount) {
      price = order.discount.totalAmountDiscounted;
    } else if (order.amount !== null) {
      price = order.amount;
    }
  } else if (product.price !== null) {
    price = product.price;
  }

  const error =
    placeSubscriptionOrderResult.error ||
    payOrderCheckoutResult.error ||
    payOrderChargeResult.error;

  const expectedError =
    (placeSubscriptionOrderResult.data &&
      placeSubscriptionOrderResult.data.placeSubscriptionOrder.__typename ===
        'SubscriptionOrderError') ||
    (payOrderChargeResult.data &&
      payOrderChargeResult.data.payOrderCharge.__typename ===
        'PaymentIntentError');

  const registrationFee =
    (placeSubscriptionOrderResult.data?.placeSubscriptionOrder.__typename ===
      'Order' &&
      placeSubscriptionOrderResult.data?.placeSubscriptionOrder.orderLines.find(
        orderLine => orderLine?.type === 'registration_fee'
      )?.amount) ||
    undefined;

  const hasMultiplePaymentMethodTypes = availablePaymentMethods.length > 1;

  const discount = order && order.discount;

  let initialAutoRenewProduct = false;
  if (
    product.renewOptMethod === ProductOptMethod.forced ||
    product.renewOptMethod === ProductOptMethod.out
  ) {
    initialAutoRenewProduct = true;
  } else if (product.renewOptMethod === ProductOptMethod.in) {
    initialAutoRenewProduct = false;
  }

  const onFormSubmit = useCallback(
    async (values: Values) => {
      if (!placeSubscriptionOrderResult.data) {
        return;
      }

      const { placeSubscriptionOrder } = placeSubscriptionOrderResult.data;
      if (placeSubscriptionOrder.__typename !== 'Order') {
        return;
      }

      if (paymentMethod && !values.changePaymentMethod) {
        setConfirmationSessionStripeError(false);
        setConfirmationSessionStripeData(undefined);
        await payOrderCharge({
          variables: {
            orderId: placeSubscriptionOrder.id,
            autoRenewProduct: values.autoRenewProduct
          }
        });

        return;
      }

      if (!values.paymentMethodType) {
        return;
      }

      await payOrderCheckout({
        variables: {
          paths: getPaths({
            asPath: router.asPath,
            token: placeSubscriptionOrder.token,
            type: PaymentDocumentType.order
          }),
          orderId: placeSubscriptionOrder.id,
          autoRenewProduct: values.autoRenewProduct,
          paymentMethodType: values.paymentMethodType
        }
      });
    },
    [
      placeSubscriptionOrderResult,
      payOrderCharge,
      payOrderCheckout,
      availablePaymentMethods,
      paymentMethod
    ]
  );

  return (
    <>
      <Box data-testid="CheckoutStep">
        <H2 typoStyle="lg" mb={0}>
          {product.name}
        </H2>
        <motion.div
          animate={discount ? 'discount' : 'notDiscount'}
          variants={{ discount: { scale: 0.6 }, notDiscount: { scale: 1 } }}
        >
          <Flex flexDirection="column">
            <ProductPrice
              typoStyle="md"
              textDecoration={discount ? 'line-through' : undefined}
              color={discount ? 'neutral.4' : 'primaryText'}
              mb={4}
              {...product}
            />
            {registrationFee && (
              <Box>
                <Text fontWeight="bold">
                  <FormattedMessage
                    id="modules.Receipt.RegistrationFee.subHeading"
                    defaultMessage="Registration fee"
                  />
                </Text>
                <RegistrationFee amount={registrationFee} />
              </Box>
            )}
          </Flex>
        </motion.div>
      </Box>
      <Box mb="sm">
        <AnimatePresence initial={false}>
          {discount && order && (
            <>
              <motion.span
                key="totalAmountDiscounted"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 2, opacity: 1 }}
                exit={{ scale: 0.5, opacity: 0 }}
              >
                <Text
                  as="span"
                  ml={2}
                  typoStyle="md"
                  color="primaryText"
                  data-testid="totalAmountDiscounted"
                >
                  <FormattedCurrency value={discount.totalAmountDiscounted} />
                </Text>
              </motion.span>
              <motion.div
                key="discountAmount"
                data-testid="discountAmount"
                initial={{ y: -10, opacity: 0 }}
                animate={{
                  y: 0,
                  opacity: 1
                }}
                exit={{ y: -10, opacity: 0 }}
              >
                <Text mute mt={2} mb={1} small>
                  <FormattedMessage id="discount" defaultMessage="Discount" />
                </Text>
                <Flex alignItems="center" justifyContent="center">
                  {discount.partner && (
                    <Box mr={2}>
                      <DiscountPartnerIcon partner={discount.partner} />
                    </Box>
                  )}
                  <Box mr={2}>{discount.name}</Box>
                  <Text mute>
                    <DiscountAmount discountAmount={discount.discountAmount} />
                  </Text>
                  <LoadableButton
                    ml="xxs"
                    shape="cool"
                    size="small"
                    variant="danger"
                    loading={revokeDiscountCodeResult.loading}
                    responseState={
                      revokeDiscountCodeResult.error ? 'error' : 'success'
                    }
                    onClick={() => {
                      revokeDiscountCode({
                        variables: { orderId: order.id }
                      });
                    }}
                  >
                    <I18NRemove />
                  </LoadableButton>
                </Flex>
              </motion.div>
            </>
          )}
          {!discount && (
            <motion.div
              initial={{ y: -10, opacity: 0 }}
              animate={{
                y: 0,
                opacity: 1
              }}
              exit={{ y: -10, opacity: 0 }}
            >
              <Box mt="sm">
                <Discount
                  orderId={order && order.id}
                  onDiscountVerified={verifiedOrder => {
                    if (client) {
                      setOrder(verifiedOrder);
                      const fragmentId = defaultDataIdFromObject({
                        id: verifiedOrder.id,
                        __typename: 'Order'
                      });
                      if (fragmentId) {
                        client.cache.writeFragment({
                          id: fragmentId,
                          fragment: PLACE_SUBSCRIPTION_ORDER_FRAGMENT,
                          data: verifiedOrder
                        });
                      }
                    }
                  }}
                />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {order && !discount && product.partners && product.partners.length > 0 && (
        <Box mb="xs">
          <PartnersForm
            partners={product.partners}
            order={order}
            onDiscountVerified={verifiedOrder => {
              if (client) {
                setOrder(verifiedOrder);
                const fragmentId = defaultDataIdFromObject({
                  id: verifiedOrder.id,
                  __typename: 'Order'
                });
                if (fragmentId) {
                  client.cache.writeFragment({
                    id: fragmentId,
                    fragment: PLACE_SUBSCRIPTION_ORDER_FRAGMENT,
                    data: verifiedOrder
                  });
                }
              }
            }}
          />
        </Box>
      )}

      <Formik<Values>
        initialValues={{
          autoRenewProduct: initialAutoRenewProduct,
          changePaymentMethod: false,
          paymentMethodType: hasMultiplePaymentMethodTypes
            ? null
            : availablePaymentMethods.length[0] || PaymentMethodType.card
        }}
        enableReinitialize
        onSubmit={onFormSubmit}
      >
        {({ values, setFieldValue }) => {
          return (
            <Form>
              <Flex
                alignItems={haveImage ? 'flex-start' : 'center'}
                flexDirection="column"
              >
                {product.durationMins !== null &&
                  product.renewOptMethod !== ProductOptMethod.disabled && (
                    <RenewOptMethod product={product} />
                  )}
                <Box maxWidth={400}>{disclaimer}</Box>
              </Flex>
              {checkoutSessionStripe && (
                <StripeCheckoutRedirect
                  sessionId={checkoutSessionStripe.sessionId}
                  publishableKey={checkoutSessionStripe.publishableKey}
                  showSpinner={false}
                />
              )}
              {confirmationSessionStripeData && (
                <StripeConfirmCardPayment
                  showSpinner={false}
                  clientSecret={confirmationSessionStripeData.clientSecret}
                  paymentMethod={confirmationSessionStripeData.paymentMethodRef}
                  token={confirmationSessionStripeData.paymentDocument.token}
                  type={PaymentDocumentType.order}
                  publishableKey={confirmationSessionStripeData.publishableKey}
                  onError={() => {
                    setConfirmationSessionStripeError(true);
                    setConfirmationSessionStripeData(undefined);
                  }}
                  onAsyncProcessCompleted={state => {
                    switch (state) {
                      case 'success': {
                        setConfirmationSessionStripeError(false);
                        refetch();
                        pushRoute({ intl, route: 'profile' });
                        break;
                      }
                      case 'error':
                      case 'cancelled':
                      case 'failed': {
                        setConfirmationSessionStripeData(undefined);
                        setConfirmationSessionStripeError(true);
                        break;
                      }
                    }
                  }}
                />
              )}
              {error && <GraphQLErrors error={error} />}
              {confirmationSessionStripeError && (
                <Alert variant="error">
                  <FormattedMessage
                    id="StripeCheckout.confirmationSessionStripe.error"
                    defaultMessage="Could not confirm your payment card. Please try again or add a new payment card."
                  />
                </Alert>
              )}
              <Box mb="xs">
                {hasMultiplePaymentMethodTypes && (
                  <Box mb="xs">
                    <PaymentMethodTypeList
                      selected={values.paymentMethodType}
                      availablePaymentMethods={availablePaymentMethods}
                      onSelect={type =>
                        setFieldValue('paymentMethodType', type)
                      }
                    />
                  </Box>
                )}

                <CheckoutButton
                  price={price}
                  responseState={expectedError || error ? 'error' : 'success'}
                  disabled={
                    !placeSubscriptionOrderResult.data ||
                    !values.paymentMethodType
                  }
                  loading={
                    payOrderChargeResult.loading ||
                    payOrderCheckoutResult.loading ||
                    !!confirmationSessionStripeData
                  }
                />
                {paymentMethod && !placeSubscriptionOrderResult.loading && (
                  <motion.div
                    transition={{ ease: 'easeInOut', duration: 0.2 }}
                    variants={{
                      visible: { y: 0, opacity: 1, visibility: 'visible' },
                      hidden: {
                        y: -8,
                        opacity: 0,
                        transitionEnd: { visibility: 'hidden' }
                      }
                    }}
                    initial={'hidden'}
                    animate={
                      values.paymentMethodType === PaymentMethodType.card
                        ? 'visible'
                        : 'hidden'
                    }
                  >
                    <PaymentCardInfo
                      paymentMethod={paymentMethod}
                      payOrderCheckoutResult={payOrderCheckoutResult}
                      placeSubscriptionOrderResult={
                        placeSubscriptionOrderResult
                      }
                    />
                  </motion.div>
                )}
              </Box>

              {!placeSubscriptionOrderResult.loading && ( // prevent from jumping if PaymentCardInfo is rendered
                <PaymentProviderInfo paymentProvider={paymentProvider} />
              )}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

const CheckoutButton = ({
  responseState,
  loading,
  disabled,
  price
}: {
  responseState?: 'success' | 'error';
  loading: boolean;
  disabled: boolean;
  price: number;
}) => {
  return (
    <LoadableButton
      data-testid="StripeCheckout__CheckoutButton"
      type="submit"
      mb="xxs"
      variant="primary"
      shape="cool"
      responseState={responseState}
      disabled={disabled}
      loading={loading}
    >
      <FormattedMessage
        {...messages.completePurchaseButton}
        values={{ priceComponent: <FormattedCurrency value={price} /> }}
      />
    </LoadableButton>
  );
};

const PaymentCardInfo = ({
  paymentMethod,
  placeSubscriptionOrderResult,
  payOrderCheckoutResult
}: {
  paymentMethod: CurrentUser_paymentMethod;
  placeSubscriptionOrderResult: MutationResult<placeSubscriptionOrderMutation>;
  payOrderCheckoutResult: MutationResult<payOrderCheckoutMutation>;
}) => {
  const { setFieldValue, submitForm } = useFormikContext<Values>();

  const onChangeClick = useCallback(() => {
    setFieldValue('changePaymentMethod', true);
    return submitForm();
  }, [setFieldValue, submitForm]);

  return (
    <Text>
      {paymentMethod.__typename === 'Card' && (
        <ChargePaymentCardMessage
          last4={paymentMethod.last4}
          displayBrand={paymentMethod.displayBrand}
        />
      )}
      <AnchorButton
        data-testid="StripeCheckout__ChangeButton"
        onClick={onChangeClick}
        disabled={
          !placeSubscriptionOrderResult || payOrderCheckoutResult.loading
        }
      >
        <FormattedMessage {...messages.change} />
      </AnchorButton>
    </Text>
  );
};
